import { useEffect, useId, useState } from 'react';

import { Permission } from '@bookora/shared';
import { IconButton, Modal } from '@bookora/ui';
import { useNavigate } from 'react-router-dom';

import { PatientPicker } from '@/components/patient-picker';
import { tourAnchor } from '@/guidance';
import { useCan } from '@/permissions/use-can';

/**
 * «جست‌وجوی بیمار» in the header — the patient file from anywhere, in one step.
 *
 * A receptionist with a patient on the phone used to go sidebar → بیماران →
 * search box → row, from whatever screen she was on. Finding a file is the
 * single most frequent thing the desk does, so it lives in the chrome.
 *
 * It is the shared `PatientPicker`, not a second search: opening it lists the
 * recent and newly registered patients before anything is typed (the desk
 * rarely wants a stranger), and typing searches name, mobile, national ID and
 * file number server-side. Picking a row opens that patient's file.
 *
 * Shortcut: Ctrl+K (⌘K) anywhere, or «/» when the focus is not in a field —
 * the two conventions office software has trained people on. Below `md` the
 * field folds into an icon that opens it in a dialog, because a 390px header
 * has room for the menu, the clinic and the bell and nothing else.
 *
 * Gated on `patient.view`: the list endpoint refuses without it, and a search
 * box that can only ever fail is worse than none.
 */
export function PatientQuickSearch({ compact }: { compact: boolean }) {
  const canView = useCan(Permission.PATIENT_VIEW);
  const navigate = useNavigate();
  const fieldId = useId();
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!canView) return;

    function handleKeyDown(event: KeyboardEvent): void {
      const target = event.target as HTMLElement | null;
      const inField =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        Boolean(target?.isContentEditable);
      const combo = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k';
      const slash = event.key === '/' && !inField && !event.ctrlKey && !event.metaKey;
      if (!combo && !slash) return;

      event.preventDefault();
      if (compact) {
        setDialogOpen(true);
        return;
      }
      document.getElementById(fieldId)?.focus();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canView, compact, fieldId]);

  // The dialog's field takes focus once the modal has painted it.
  useEffect(() => {
    if (!dialogOpen) return;
    const frame = requestAnimationFrame(() =>
      document.getElementById(`${fieldId}-dialog`)?.focus(),
    );
    return () => cancelAnimationFrame(frame);
  }, [dialogOpen, fieldId]);

  if (!canView) return null;

  function openPatient(patientId: string | null): void {
    if (!patientId) return;
    setDialogOpen(false);
    void navigate(`/patients/${patientId}`);
  }

  if (compact) {
    return (
      <>
        <IconButton
          {...tourAnchor('shell.search')}
          type="text"
          name="search"
          label="جست‌وجوی بیمار"
          // It opens the search dialog immediately; no bubble in front of it.
          tooltip={false}
          onClick={() => setDialogOpen(true)}
        />
        <Modal
          open={dialogOpen}
          onCancel={() => setDialogOpen(false)}
          title="جست‌وجوی بیمار"
          footer={null}
          destroyOnHidden
        >
          <PatientPicker
            id={`${fieldId}-dialog`}
            value={null}
            onChange={openPatient}
            placeholder="نام، موبایل یا شمارهٔ پرونده"
            ariaLabel="جست‌وجوی بیمار با نام، موبایل یا شمارهٔ پرونده"
            style={{ width: '100%' }}
          />
        </Modal>
      </>
    );
  }

  return (
    // No tooltip for the shortcut: one hovering over the field would sit on
    // top of the list the field opens. The shortcut is in the accessible name.
    <div className="bk-quick-search" {...tourAnchor('shell.search')}>
      <PatientPicker
        id={fieldId}
        value={null}
        onChange={openPatient}
        placeholder="جست‌وجوی بیمار (نام یا موبایل)"
        ariaLabel="جست‌وجوی بیمار با نام، موبایل یا شمارهٔ پرونده؛ میان‌بر Ctrl+K"
        style={{ width: '100%' }}
      />
    </div>
  );
}
